import { authClient } from "@/lib/auth-client";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/forgot-password")({
	component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
	const [email, setEmail] = useState("");
	const [loading, setLoading] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			// We intentionally don't reveal whether the email matched a real
			// account — the UI shows the same success state either way.
			await authClient.requestPasswordReset({
				email,
				redirectTo: `${window.location.origin}/reset-password`,
			});
			setSubmitted(true);
		} catch {
			setError("Something went wrong. Try again in a moment.");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="flex min-h-screen items-center justify-center bg-[#f8f9fb]">
			<div className="w-full max-w-sm space-y-6 rounded-lg border border-[#e5e7eb] bg-white p-8 shadow-sm">
				<div className="text-center">
					<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#16a34a]">
						<span className="text-lg font-bold text-white">P</span>
					</div>
					<h1 className="text-xl font-semibold text-[#1e1e20]">Reset your password</h1>
					<p className="mt-1 text-sm text-[#6b7280]">
						Enter your email and we'll send you a reset link.
					</p>
				</div>

				{submitted ? (
					<div className="space-y-4">
						<div className="rounded bg-[#f0fdf4] p-3 text-sm text-[#166534]">
							If an account exists for <strong>{email}</strong>, a reset link is on its way. It
							expires in 1 hour.
						</div>
						<Link
							to="/login"
							className="flex h-9 w-full items-center justify-center rounded border border-[#d1d5db] text-sm font-medium text-[#1e1e20] hover:bg-[#f3f4f6]"
						>
							Back to sign in
						</Link>
					</div>
				) : (
					<form onSubmit={handleSubmit} className="space-y-4">
						{error && (
							<div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{error}</div>
						)}

						<div className="space-y-1">
							<label htmlFor="email" className="text-xs font-medium text-[#1e1e20]">
								Email
							</label>
							<input
								id="email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
								placeholder="you@example.com"
							/>
						</div>

						<button
							type="submit"
							disabled={loading}
							className="h-9 w-full rounded bg-[#16a34a] text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#15803d] disabled:opacity-50"
						>
							{loading ? "Sending…" : "Send reset link"}
						</button>

						<Link
							to="/login"
							className="block text-center text-xs text-[#6b7280] hover:text-[#1e1e20]"
						>
							Back to sign in
						</Link>
					</form>
				)}
			</div>
		</div>
	);
}
